import { supabase } from '../lib/supabase';

export class EmployeeService {
  /**
   * Get all employees for admin management
   * @returns {Promise<Array>} Array of employee objects with their details
   */
  static async getAllEmployees() {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select(`
          id,
          name,
          email,
          email_signature_html,
          slack_userid,
          abconnect_user_id,
          status_mentions,
          roles,
          phone,
          created_at
        `)
        .order('name', { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch employees: ${error.message}`);
      }

      // Return all employees including deactivated for admin management
      return data || [];
    } catch (error) {
      console.error('Error fetching employees:', error);
      throw error;
    }
  }

  /**
   * Update employee Slack ID
   * @param {number} employeeId - The employee ID
   * @param {string} slackId - The Slack ID
   * @returns {Promise<Object>} Updated employee object
   */
  static async updateEmployeeSlackId(employeeId, slackId) {
    try {
      const { data, error } = await supabase
        .from('employees')
        .update({ slack_userid: slackId })
        .eq('id', employeeId)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to update Slack ID: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error updating Slack ID:', error);
      throw error;
    }
  }

  /**
   * Update employee status mentions
   * @param {number} employeeId - The employee ID
   * @param {Array<string>} statusMentions - Array of status mentions
   * @returns {Promise<Object>} Updated employee object
   */
  static async updateEmployeeStatusMentions(employeeId, statusMentions) {
    try {
      const { data, error } = await supabase
        .from('employees')
        .update({ status_mentions: statusMentions })
        .eq('id', employeeId)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to update status mentions: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error updating status mentions:', error);
      throw error;
    }
  }

  /**
   * Update employee profile information
   * @param {number} employeeId - The employee ID
   * @param {Object} updates - The fields to update
   * @returns {Promise<Object>} Updated employee object
   */
  static async updateEmployee(employeeId, updates) {
    try {
      const { data, error } = await supabase
        .from('employees')
        .update(updates)
        .eq('id', employeeId)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to update employee: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error updating employee:', error);
      throw error;
    }
  }

  /**
   * Update employee roles
   * @param {number} employeeId - The employee ID
   * @param {Array<string>} roles - Array of roles
   * @returns {Promise<Object>} Updated employee object
   */
  static async updateEmployeeRoles(employeeId, roles) {
    try {
      const { data, error } = await supabase
        .from('employees')
        .update({ roles: roles })
        .eq('id', employeeId)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to update roles: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error updating roles:', error);
      throw error;
    }
  }

  /**
   * Get available status mentions options
   * @returns {Array<string>} Array of available status mentions
   */
  static getAvailableStatusMentions() {
    return [
      'Admin',
      'Operations Manager',
      'Model Building Team',
      'Takeoffs',
      'Office Admin',
      'Deactivated',
      'Manager',
      'High Priority',
      'Remote',
      'Part Time'
    ];
  }

  /**
   * Get available employee roles
   * @returns {Array<string>} Array of available roles
   */
  static getAvailableRoles() {
    return [
      'Operations Manager',
      'Model Building Manager',
      'Sales Manager',
      'Client Support Manager',
      'New Project Setup Specialist',
      'Data Prep Specialist',
      'Model Builder',
      'Utilities Specialist',
      'Discrepancy Checker',
      'Quality Control Specialist',
      'Export and Send Specialist',
      'Financial Manager',
      'Accounts Receivable Specialist',
      'Accounts Payable Specialist',
      'Bookkeeper',
      'Business Administrator',
      'Human Resources Manager',
      'Payroll Specialist',
      'Contracts Specialist',
      'Legal Representative',
      'Remote Support Specialist',
      'Project Control Specialist',
      'Revision Process Specialist',
      'Admin' // Add explicit Admin role
    ];
  }

  /**
   * Check if employee is active (not deactivated)
   * @param {Array<string>} statusMentions - Employee's status mentions
   * @returns {boolean} True if active
   */
  static isEmployeeActive(statusMentions) {
    return !statusMentions?.includes('Deactivated');
  }

  /**
   * Get employee roles by email for auth system
   * @param {string} email - Employee's email address
   * @returns {Promise<Array<string>|null>} Array of roles or null if not found
   */
  static async getEmployeeRolesByEmail(email) {
    try {
      if (!email) {
        return null;
      }

      const { data, error } = await supabase
        .from('employees')
        .select('roles, status_mentions')
        .eq('email', email.toLowerCase())
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned - employee not found
          return null;
        }
        throw new Error(`Failed to fetch employee roles: ${error.message}`);
      }

      // Check if employee is deactivated via status_mentions
      if (data.status_mentions?.includes('Deactivated')) {
        return ['deactivated'];
      }

      // Return roles array or empty array if no roles
      return data.roles || [];
    } catch (error) {
      console.error('Error fetching employee roles by email:', error);
      // Return null to indicate lookup failed, not that user has no roles
      return null;
    }
  }

  /**
   * Map employee roles to auth system roles for board access
   * @param {Array<string>} employeeRoles - Array of employee roles
   * @returns {string} Auth system role
   */
  static mapEmployeeRolesToAuthRole(employeeRoles) {
    if (!employeeRoles || employeeRoles.length === 0) {
      return 'model_building_team'; // Default role
    }

    // Check for admin roles first (highest priority)
    if (employeeRoles.includes('Business Administrator') || employeeRoles.includes('Admin')) {
      return 'admin';
    }

    // Check for operations manager
    if (employeeRoles.includes('Operations Manager')) {
      return 'operations_manager';
    }

    // Check for office admin roles
    if (employeeRoles.some(role => [
      'Human Resources Manager',
      'Financial Manager',
      'Accounts Receivable Specialist',
      'Accounts Payable Specialist',
      'Bookkeeper',
      'Payroll Specialist'
    ].includes(role))) {
      return 'office_admin'; // Office admin access
    }

    // Check for takeoffs roles
    if (employeeRoles.includes('New Project Setup Specialist')) {
      return 'takeoffs';
    }

    // Default to model building team for all other roles
    return 'model_building_team';
  }
}